import React, { useCallback, useEffect, useRef } from "react";
import { connect } from "react-redux";
import FormField from '../../../../../dynamicFormBuilder/FormField';
import { loadLookupFromStaticDataSource } from '../../../../../common/Utils';
import { SetupLayout } from '../Layout';
import {
    storageStartDateOptions,
    restartStorageDatesOptions,
    expDateBasedOnOptions,
    rateScheduleFilterOptions,
    EXP_DATE_BASED_ON,
} from '../options';

const ADD_DAYS_FIELD = 'ADDTOEXPDATE';
const BASED_ON_FIELD = 'EXPDATEBASEDON';

// How long to keep looking for the Add Days widget after the form reports itself loaded.
const APPLY_RETRY_MS = 100;
const APPLY_MAX_ATTEMPTS = 10;

function SetupTab(props) {
    var currentUser = props.oidc?.user;
    const DYNAMICFORMSETTING = props.oidc?.user?.profile?.DYNAMICFORMSETTING;

    const basedOn = props.formFields?.[BASED_ON_FIELD];
    const addDaysDisabled = basedOn === EXP_DATE_BASED_ON.NONE;

    const { formFields, formRefs, handleFieldChange } = props;
    const applyTimer = useRef(null);

    const loadOptions = useCallback(() => {
        loadLookupFromStaticDataSource("STORAGESTARTDATE", storageStartDateOptions, formFields, formRefs, 'id', 'text');
        loadLookupFromStaticDataSource("RESTARTSTORAGEDATES", restartStorageDatesOptions, formFields, formRefs, 'id', 'text');
        loadLookupFromStaticDataSource(BASED_ON_FIELD, expDateBasedOnOptions, formFields, formRefs, 'id', 'text');
        loadLookupFromStaticDataSource("RATESCHEDULEFILTER", rateScheduleFilterOptions, formFields, formRefs, 'id', 'text');
    }, [formFields, formRefs]);

    /**
     * uWMD0003.edtBasedOnChange: Add Days is disabled while "Based on" is None, enabled otherwise.
     * Returns whether the widget was actually there to be updated.
     *
     * Fields.js registers each DevExtreme widget in formRefs under its bare field name, so the key is
     * right - the difficulty is only ever *when* the widget exists.
     */
    const applyAddDaysDisabled = useCallback(() => {
        const ref = formRefs?.current?.[ADD_DAYS_FIELD];
        if (!ref || typeof ref.instance !== 'function') return false;

        const instance = ref.instance();
        if (!instance || typeof instance.option !== 'function') return false;

        instance.option('disabled', addDaysDisabled);
        return true;
    }, [formRefs, addDaysDisabled]);

    /**
     * The rule has to be applied from here, not only from an effect.
     *
     * Delphi runs edtBasedOnChange from qeIMItemDPAfterScroll, i.e. on load as well as on change. The
     * React equivalent is not an effect on the value: EXPDATEBASEDON is already 'N' on the first
     * render of both Add (the seeded default) and Edit (the stored value), so the flag never
     * transitions and an effect keyed on it fires once - at a point where FormField has not yet
     * rendered its fields, so formRefs is still empty and there is nothing to disable. Selecting
     * "None" by hand worked precisely because that *is* a transition. That was the reported bug.
     *
     * FormField renders its fields asynchronously and its own re-render does not re-render this
     * component, so onFormLoad is the only callback that fires at the right moment - and it runs from
     * fetchData's finally, which can still precede the commit that mounts the fields. Hence the
     * bounded retry rather than a single guessed delay.
     */
    const handleFormLoad = useCallback(() => {
        setTimeout(loadOptions, APPLY_RETRY_MS);

        let attempts = 0;
        const tryApply = () => {
            if (applyAddDaysDisabled() || attempts >= APPLY_MAX_ATTEMPTS) return;
            attempts += 1;
            applyTimer.current = setTimeout(tryApply, APPLY_RETRY_MS);
        };
        tryApply();
    }, [loadOptions, applyAddDaysDisabled]);

    // Re-assert on every subsequent render of this component: the record finishing its load, and the
    // user changing "Based on", both land here.
    useEffect(() => {
        loadOptions();
        applyAddDaysDisabled();
    }, [loadOptions, applyAddDaysDisabled]);

    useEffect(() => () => clearTimeout(applyTimer.current), []);

    const handleFieldValueChange = useCallback((name, value, e) => {
        handleFieldChange(name, value, e);

        // uWMM0003.qryIMItemDPEXPDATEBASEDONChange: selecting None clears Add Days. Only on an actual
        // change - Delphi's AfterScroll path adjusts the control's enabled state but never clears the
        // stored value. The server applies the same rule, so a direct API call cannot bypass it.
        if (name === BASED_ON_FIELD && value === EXP_DATE_BASED_ON.NONE) {
            handleFieldChange(ADD_DAYS_FIELD, null);
        }
    }, [handleFieldChange]);

    return (
        <div>
            <FormField
                currrentUser={currentUser}
                layoutId='9E5B3A76-1C42-4D89-A0F5-6B2E8C4D7F31'
                layoutName='WMItemDataProfileSetupForm'
                defaultFormSchema={SetupLayout}
                formFields={props.formFields}
                setformFields={props.setformFields}
                handleFieldValueChange={handleFieldValueChange}
                DYNAMICFORMSETTING={DYNAMICFORMSETTING}
                formRefs={props.formRefs}
                onFormLoad={handleFormLoad}
            />
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc
    };
}

export default connect(mapStateToProps)(SetupTab);
